import { Link, useLocation } from 'react-router-dom';
import { Badge, Avatar, Spinner, Input } from '@klaro7/ui';

import { useAdminProfessionals } from '@/features/professionals/hooks/useAdminProfessionals';
import { ResendCredentialsButton } from '@/features/professionals/components/ResendCredentialsButton';
import { resolveUploadUrl } from '@/lib/uploads';
import { useDebouncedUrlSearch, useListUrlState } from '@/lib/listUrlState';
import type { AdminProfessionalQueryParams } from '@/features/professionals/services/professional-admin.service';

const statusConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'info' }> = {
  verified: { label: 'Pubblicato', variant: 'success' },
  in_review: { label: 'Da revisionare', variant: 'warning' },
  pending: { label: 'In compilazione', variant: 'warning' },
  rejected: { label: 'Rifiutato', variant: 'error' },
};

// Virtual sub-state of pending: questionnaire approved, consultant not yet published.
const approvedStatus = { label: 'Approvato', variant: 'info' as const };

// Presence dot on the avatar: green=online, amber=busy (in chiamata), grey=offline.
const onlineDotConfig: Record<string, { cls: string; label: string }> = {
  online: { cls: 'bg-green-500', label: 'Online' },
  busy: { cls: 'bg-amber-500', label: 'In chiamata' },
  paused: { cls: 'bg-brand-orange', label: 'In pausa' },
  offline: { cls: 'bg-neutral-300', label: 'Offline' },
};

export function AdminProfessionalsPage() {
  const location = useLocation();
  const { get, set, page } = useListUrlState();
  const statusFilter = get('status');
  const presenceFilter = get('presence');
  const debouncedSearch = get('q');
  // Ordinamento: per data di registrazione (default) o per numero di preferiti.
  const sortBy = get('sort') === 'favoriteCount' ? 'favoriteCount' : 'createdAt';
  const [search, setSearch] = useDebouncedUrlSearch(debouncedSearch, (q) => set({ q }));
  const setPage = (p: number) => set({ page: p });
  const detailState = { from: location.pathname + location.search };

  const params: AdminProfessionalQueryParams = {
    page,
    limit: 20,
    sortBy,
    sortOrder: 'desc',
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(statusFilter ? { verificationStatus: statusFilter as 'pending' | 'in_review' | 'verified' | 'rejected' | 'approved' } : {}),
    ...(presenceFilter ? { onlineStatus: presenceFilter as 'online' | 'busy' | 'offline' | 'paused' } : {}),
  };

  const { data, isLoading } = useAdminProfessionals(params);

  const professionals = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Professionisti</h1>
        <p className="mt-1 text-sm text-neutral-600">Gestisci i professionisti e le verifiche</p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-80">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca nome, nome d'arte, email, titolo, specializzazione"
            className="h-9 pr-9"
            leftIcon={(
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
              </svg>
            )}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
              aria-label="Cancella ricerca"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <select
          value={statusFilter}
          onChange={(e) => set({ status: e.target.value })}
          className="h-9 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 focus:border-brand-teal/50 focus:outline-none"
        >
          <option value="">Tutti gli stati</option>
          <option value="pending">In compilazione</option>
          <option value="approved">Approvati</option>
          <option value="in_review">Da revisionare</option>
          <option value="verified">Pubblicati</option>
          <option value="rejected">Rifiutati</option>
        </select>
        <select
          value={presenceFilter}
          onChange={(e) => set({ presence: e.target.value })}
          className="h-9 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-neutral-900 focus:border-brand-teal/50 focus:outline-none"
        >
          <option value="">Qualsiasi presenza</option>
          <option value="online">Solo online</option>
          <option value="busy">In chiamata</option>
          <option value="paused">In pausa</option>
          <option value="offline">Offline</option>
        </select>
        {pagination && (
          <span className="text-sm text-neutral-400">
            {pagination.total} professionist{pagination.total === 1 ? 'a' : 'i'}
          </span>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-50">
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Professionista</th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Titolo</th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Specializzazioni</th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Stato</th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">
                    <button
                      type="button"
                      onClick={() => set({ sort: sortBy === 'favoriteCount' ? null : 'favoriteCount' })}
                      className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-brand-teal ${
                        sortBy === 'favoriteCount' ? 'text-brand-teal' : ''
                      }`}
                      title="Ordina per numero di clienti che hanno salvato il consulente"
                    >
                      Preferiti
                      {sortBy === 'favoriteCount' && <span aria-hidden="true">↓</span>}
                    </button>
                  </th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Registrato</th>
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {professionals.filter((pro) => pro.userId).map((pro) => {
                  const status = pro.verificationStatus === 'pending' && pro.questionnaireStatus === 'approved'
                    ? approvedStatus
                    : statusConfig[pro.verificationStatus] ?? { label: pro.verificationStatus, variant: 'warning' as const };
                  const fullName = `${pro.userId?.profile?.firstName ?? ''} ${pro.userId?.profile?.lastName ?? ''}`.trim() || '—';
                  const artName = `${pro.userId?.profile?.artFirstName ?? ''} ${pro.userId?.profile?.artLastName ?? ''}`.trim();
                  const dot = onlineDotConfig[pro.onlineStatus] ?? { cls: 'bg-neutral-300', label: 'Offline' };

                  return (
                    <tr key={pro.id} className="transition-colors hover:bg-neutral-50/50">
                      <td className="px-5 py-3.5">
                        <Link to={`/dashboard/admin/professionals/${pro.id}`} state={detailState} className="flex items-center gap-3 group">
                          <span className="relative inline-flex shrink-0">
                            <Avatar src={resolveUploadUrl(pro.userId?.profile?.avatar)} fallback={fullName} size="sm" />
                            <span
                              title={dot.label}
                              className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-white ${dot.cls}`}
                            />
                          </span>
                          <div>
                            <p className="font-medium text-neutral-900 group-hover:text-brand-teal transition-colors">
                              {fullName}
                              {artName && <span className="ml-1.5 text-xs font-normal text-brand-teal">in arte {artName}</span>}
                            </p>
                            <p className="text-xs text-neutral-600">{pro.userId?.email ?? '—'}</p>
                          </div>
                        </Link>
                      </td>
                      <td className="px-5 py-3.5 text-sm font-medium text-neutral-700">{pro.title}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex flex-wrap gap-1">
                          {pro.specializations.slice(0, 3).map((spec) => (
                            <Badge key={spec} variant="outline" className="text-xs">
                              {spec}
                            </Badge>
                          ))}
                          {pro.specializations.length > 3 && (
                            <span className="text-xs text-neutral-400">+{pro.specializations.length - 3}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <Badge variant={status.variant}>{status.label}</Badge>
                          {pro.isSuspended && <Badge variant="error">Sospeso</Badge>}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-neutral-600">{pro.favoriteCount ?? 0}</td>
                      <td className="px-5 py-3.5 text-sm text-neutral-600">
                        {new Date(pro.createdAt).toLocaleDateString('it-IT', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          {(pro.verificationStatus === 'pending' || pro.verificationStatus === 'in_review') && (
                            <Link
                              to={`/dashboard/admin/professionals/${pro.id}`} state={detailState}
                              className="rounded-lg border border-brand-teal/40 px-3 py-1.5 text-xs font-semibold text-brand-teal transition-colors hover:bg-brand-teal/10"
                            >
                              Revisiona
                            </Link>
                          )}
                          {pro.verificationStatus === 'rejected' && (
                            <Link
                              to={`/dashboard/admin/professionals/${pro.id}`} state={detailState}
                              className="rounded-lg border border-neutral-200 px-3 py-1.5 text-xs font-semibold text-neutral-600 transition-colors hover:bg-neutral-50"
                            >
                              Dettagli
                            </Link>
                          )}
                          {pro.verificationStatus === 'verified' && (
                            <>
                              <span className="text-xs text-neutral-400">Attivo</span>
                              <ResendCredentialsButton id={pro.id} />
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {professionals.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-neutral-100">
                <svg className="h-6 w-6 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0" />
                </svg>
              </div>
              <p className="mt-4 font-medium text-neutral-700">Nessun professionista trovato</p>
              <p className="mt-1 text-sm text-neutral-600">
                {debouncedSearch || statusFilter || presenceFilter ? 'Nessun risultato per questi filtri' : 'I professionisti registrati appariranno qui'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            disabled={!pagination.hasPrevPage}
            onClick={() => setPage(page - 1)}
            className="rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm text-neutral-600 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-30"
          >
            Precedente
          </button>
          <span className="px-4 text-sm text-neutral-400">
            {pagination.page} / {pagination.totalPages}
          </span>
          <button
            disabled={!pagination.hasNextPage}
            onClick={() => setPage(page + 1)}
            className="rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm text-neutral-600 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-30"
          >
            Successiva
          </button>
        </div>
      )}
    </div>
  );
}
